<template>
  <b-modal ref="applianceModal" v-on:show="checkEdit" v-on:hide="resetValues" size="lg" hide-footer centered>
    <loading v-bind:active.sync="loading"></loading>
    <div class="container">
      <div class="row">
        <div class="col-md-4">
          <select v-model="room" class="form-control input-group">
            <option disabled>Select Room</option>
            <option v-for="roomName in rooms" v-bind:key="roomName">{{ roomName }}</option>
          </select>
        </div>
        <div class="col-md-4 input-group" v-if="room === 'Other'">
          <input type="text" class="form-control" v-model="userRoomName" placeholder="Enter Room Name"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <select v-model="appliance" class="form-control input-group">
            <option disabled>Select Appliance</option>
            <option v-for="applianceOption in applianceOptions" v-bind:key="applianceOption.id">{{ applianceOption }}</option>
          </select>
        </div>
        <div class="col-4" v-if="appliance === 'Other'">
          <input type="text" class="form-control" v-model="userApplianceName" placeholder="Enter Appliance Name"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <purchase-location class="input-group" v-bind:stylingClasses="'form-control form-control-md square-left'" v-bind:initialSearchTerm="purchaseLocationName" v-bind:placeholder="'Purchase Location'" v-on:suggestionSelected="updatePurchaseLocation"></purchase-location>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <input type="text" class="form-control input-group" v-model="brand" placeholder="Enter Brand Name"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <input type="text" class="form-control input-group" v-model="modelNumber" placeholder="Enter Product/Model Number"/>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <input type="text" class="form-control input-group" v-model="serialNumber" placeholder="Enter Serial Number"/>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-4">
          <date-picker v-model="purchaseDate">
            <div class="input-group" slot-scope="{ inputValue, updateValue }">
              <div class="input-group-prepend">
                <span class="input-group-text"><i class="fas fa-calendar"></i></span>
              </div>
              <input type="text" class="form-control square-left white-background" v-bind:value="inputValue" v-on:change.native="updateValue($event.target.value)" placeholder="Purchase Date" disabled>
            </div>
          </date-picker>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-2">
          <div class="input-group no-margin">
            <div class="input-group-prepend">
              <span class="input-group-text note-icon"><i class="fa fa-sticky-note"></i></span>
            </div>
            <p class="form-control note-label">Note</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col my-auto">
          <div class="input-group">
            <input type="textarea" v-model="note" class="form-control note-input" rows=3 placeholder="Type notes here..."/>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <p class="no-margin">*Total file size must be less than 6MB</p>
        </div>
      </div>
      <div class="row">
        <div class="col input-spacing">
          <label class="custom-file-label mx-3" for="customFile">Upload files*</label>
          <input class="custom-file-input" id="customFile" type="file" multiple accept="image/png, image/jpeg" v-on:change="previewImage">
          <img v-for="(image, index) in attachments" class="preview-image" v-bind:key="index" v-bind:src="signedAttachments[index]" v-on:click="removeImage(index)" height="100px">
        </div>
      </div>
      <div class="row">
        <div class="col-4 offset-2">
          <button class="btn btn-primary" v-on:click="createAppliance()">Save</button>
        </div>
        <div class="col-4">
          <button class="btn btn-secondary" v-on:click="$refs.applianceModal.hide()">Close</button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Money } from 'v-money'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import S3 from 'aws-sdk/clients/s3'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import PurchaseLocation from '../../../general/inputs/GoogleTextSearch.vue'

export default {
  components: {
    DatePicker,
    Loading,
    Money,
    PurchaseLocation
  },
  data () {
    return {
      loading: false,
      uid: null,
      room: 'Select Room',
      userRoomName: null,
      appliance: 'Select Appliance',
      userApplianceName: null,
      purchaseLocationAddress: null,
      purchaseLocationId: null,
      purchaseLocationName: null,
      brand: null,
      modelNumber: null,
      serialNumber: null,
      purchaseDate: null,
      note: null,
      attachments: [],
      moneyProperties: {
        decimal: '.',
        thousands: ',',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      }
    }
  },
  props: {
    applianceOptions: Array,
    editAppliance: {
      type: Object,
      required: false
    },
    editIndex: {
      type: Number,
      required: false
    },
    propertyId: String,
    rooms: Array
  },
  computed: {
    ...mapGetters([
      'getCognitoId'
    ]),
    signedAttachments () {
      let s3 = new S3()
      let signedAttachments = []
      this.attachments.forEach((photoName) => {
        if (!photoName.includes(this.propertyId)) {
          signedAttachments.push(photoName)
          return
        }
        let params = {
          Bucket: window.CONFIG.ApplianceAttachmentsBucketName,
          Key: photoName,
          Expires: 10
        }
        let signedUrl = s3.getSignedUrl('getObject', params)
        signedAttachments.push(signedUrl)
      })
      return signedAttachments
    }
  },
  methods: {
    ...mapMutations('property', [
      'addToDetailedAppliances',
      'replaceIndexFromDetailedAppliances'
    ]),
    ...mapActions('property', [
      'getProperty',
      'putPropertyData'
    ]),
    updatePurchaseLocation (datum) {
      if (typeof datum === 'object' && datum !== null) {
        if (datum.formatted_address) this.purchaseLocationAddress = datum.formatted_address
        if (datum.place_id) this.purchaseLocationId = datum.place_id
        if (datum.name) this.purchaseLocationName = datum.name
      } else {
        this.purchaseLocationAddress = null
        this.purchaseLocationId = null
        this.purchaseLocationName = datum
      }
    },
    async checkEdit () {
      // Fill in existing values if editing
      if (!this.editAppliance) return
      if (this.editAppliance) this.edit = true
      if (this.editAppliance.uid) this.uid = this.editAppliance.uid
      if (this.editAppliance.room) this.room = this.editAppliance.room
      if (this.editAppliance.appliance) this.appliance = this.editAppliance.appliance
      if (this.editAppliance.purchase_location_address) this.purchaseLocationAddress = this.editAppliance.purchase_location_address
      if (this.editAppliance.purchase_location_id) this.purchaseLocationId = this.editAppliance.purchase_location_id
      if (this.editAppliance.purchase_location_name) this.purchaseLocationName = this.editAppliance.purchase_location_name
      if (this.editAppliance.brand) this.brand = this.editAppliance.brand
      if (this.editAppliance.model_number) this.modelNumber = this.editAppliance.model_number
      if (this.editAppliance.serial_number) this.serialNumber = this.editAppliance.serial_number
      if (this.editAppliance.purchase_date) {
        this.purchaseDate = new Date(this.editAppliance.purchase_date + 'Z')
      }
      if (this.editAppliance.note) this.note = this.editAppliance.note
      if (this.editAppliance.attachments) {
        this.attachments = []
        this.attachments.push(...this.editAppliance.attachments)
      }
    },
    async createAppliance () {
      this.loading = true
      try {
        let selectedAppliance = this.appliance
        if (this.appliance === 'Select Appliance') selectedAppliance = null
        if (this.appliance === 'Other' && this.userApplianceName) selectedAppliance = this.userApplianceName
        let room = this.room
        if (room === 'Select Room') room = null
        if (room === 'Other' && this.userRoomName) room = this.userRoomName
        let formattedDate = null
        if (typeof this.purchaseDate === 'object' && this.purchaseDate !== null) {
          formattedDate = this.purchaseDate.toISOString().replace('Z', '')
        } else {
          formattedDate = this.purchaseDate
        }
        var applianceObject = {
          uid: this.uid,
          room: room,
          appliance: selectedAppliance,
          purchase_location_address: this.purchaseLocationAddress,
          purchase_location_id: this.purchaseLocationId,
          purchase_location_name: this.purchaseLocationName,
          brand: this.brand,
          model_number: this.modelNumber,
          serial_number: this.serialNumber,
          purchase_date: formattedDate,
          note: this.note,
          attachments: this.attachments
        }
        if (this.edit) {
          let payload = {
            index: this.editIndex,
            applianceObject: applianceObject
          }
          this.replaceIndexFromDetailedAppliances(payload)
        } else {
          this.addToDetailedAppliances(applianceObject)
        }
        await this.putPropertyData({ uniqueId: this.propertyId })
        await this.getProperty(this.propertyId)
        this.$refs.applianceModal.hide()
      } catch (error) {
        console.log(error)
      }
      this.loading = false
    },
    previewImage: function (event) {
      var input = event.target
      var that = this
      for (var i = 0; i < input.files.length; i++) {
        if (input.files && input.files[i]) {
          var reader = new FileReader()
          reader.onload = function (e) {
            if (!that.attachments.includes(e.target.result)) {
              var img = new Image()
              img.onload = function () {
                if (this.width > 150 && this.height > 150) {
                  that.attachments.push(e.target.result)
                } else {
                  that.imageTooSmall = true
                }
              }
            }
            img.src = e.target.result
          }
          reader.readAsDataURL(input.files[i])
        }
      }

      var fileInput = document.getElementById('customFile')
      fileInput.value = null
    },
    removeImage (index) {
      this.attachments.splice(index, 1)
    },
    resetValues () {
      this.$emit('resetEditApplianceValues')
      this.edit = false
      this.room = 'Select Room'
      this.appliance = 'Select Appliance'
      this.purchaseLocationAddress = null
      this.purchaseLocationId = null
      this.purchaseLocationName = null
      this.brand = null
      this.modelNumber = null
      this.serialNumber = null
      this.purchaseDate = null
      this.note = null
      this.attachments = []
    }
  },
  watch: {
    editAppliance () {
      this.checkEdit()
    }
  }
}
</script>

<style scoped>
.btn {
  width: 100%;
}

.input-group {
  margin-bottom: 0.5rem;
}

.input-spacing {
  margin-bottom: 0.5rem;
}

.no-margin {
  margin: 0;
}

.note-icon {
  border-radius: 0;
  border-top-left-radius: 0.25rem;
  margin-bottom: 0;
}

.note-input {
  border-top-left-radius: 0;
}

.note-label {
  border-radius: 0;
  border-top-right-radius: 0.25rem;
  margin-bottom: 0;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #FEEDAA;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}
/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.white-background {
  background-color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/home/homeData/dropdowns/ApplianceModal.vue